import { createContext, useContext, useCallback, useMemo, useRef, useState, useEffect } from 'react';
import styles from './Toast.module.css';

/**
 * Toast — non-blocking, auto-dismissing status feedback (UX §8/§11).
 *
 * Wrap the app once:  <ToastProvider><App /></ToastProvider>
 * Then anywhere:      const toast = useToast();
 *                     toast.success('Clinic activated');
 *                     toast.error('Could not delete — try again');
 *
 * Announced to screen readers via an aria-live region. All colours come from
 * existing theme tokens; nothing here changes the colour theme.
 */

const ToastCtx = createContext(null);

export function useToast() {
  const ctx = useContext(ToastCtx);
  if (!ctx) {
    // Fail soft: never crash a feature just because the provider is missing.
    // Fall back to the console so messages aren't silently lost in dev.
    return {
      show: (m) => console.warn('[toast]', m),
      success: (m) => console.warn('[toast:success]', m),
      error: (m) => console.warn('[toast:error]', m),
      info: (m) => console.warn('[toast:info]', m),
      warn: (m) => console.warn('[toast:warn]', m),
      dismiss: () => {},
    };
  }
  return ctx;
}

const ICONS = { success: '✓', error: '!', info: 'i', warn: '!' };
let _seq = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const dismiss = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
    const timer = timers.current[id];
    if (timer) { clearTimeout(timer); delete timers.current[id]; }
  }, []);

  const push = useCallback((message, { type = 'info', duration = 4000, action } = {}) => {
    if (!message) return null;
    const id = ++_seq;
    setToasts((list) => [...list, { id, message: String(message), type, action }]);
    if (duration > 0) {
      timers.current[id] = setTimeout(() => dismiss(id), duration);
    }
    return id;
  }, [dismiss]);

  // Clear any pending timers on unmount.
  useEffect(() => () => {
    Object.values(timers.current).forEach(clearTimeout);
    timers.current = {};
  }, []);

  const api = useMemo(() => ({
    show: push,
    // Errors linger a little longer and are more likely to need an action.
    success: (m, o) => push(m, { ...o, type: 'success' }),
    error:   (m, o) => push(m, { duration: 6000, ...o, type: 'error' }),
    info:    (m, o) => push(m, { ...o, type: 'info' }),
    warn:    (m, o) => push(m, { ...o, type: 'warn' }),
    dismiss,
  }), [push, dismiss]);

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div
        className={styles.viewport}
        role="region"
        aria-label="Notifications"
        aria-live="polite"
        aria-atomic="false"
      >
        {toasts.map((t) => (
          <div key={t.id} className={`${styles.toast} ${styles[t.type] || ''}`}>
            <span className={styles.icon} aria-hidden="true">{ICONS[t.type] || 'i'}</span>
            <span className={styles.msg}>{t.message}</span>
            {t.action && (
              <button
                type="button"
                className={styles.action}
                onClick={() => { try { t.action.onClick?.(); } finally { dismiss(t.id); } }}
              >
                {t.action.label}
              </button>
            )}
            <button
              type="button"
              className={styles.close}
              aria-label="Dismiss notification"
              onClick={() => dismiss(t.id)}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
